import React, { useState, useEffect, useRef } from "react";
import "../styles/pagination/pagination.css";
import { useTranslation } from "react-i18next";

// -------------------- Fixed Helper Function --------------------
function getVisiblePages(currentPage, totalPages) {
    if (totalPages <= 1) return [1];

    const pages = [];
    const delta = 1; // pages around current

    // Always add first page
    pages.push(1);

    // Determine range around current page
    let start = Math.max(2, currentPage - delta);
    let end = Math.min(totalPages - 1, currentPage + delta);

    // Add ellipsis after first page if needed
    if (start > 2) {
        pages.push("...");
    }

    // Add page numbers in the calculated range
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
        pages.push("...");
    }

    // Always add last page if there's more than one page
    if (totalPages > 1) {
        pages.push(totalPages);
    }

    return pages;
}

// -------------------- Component --------------------
export default function FilePagination({
    currentPage,
    totalPages,
    totalItems,
    onPageChange,
    startNumber = 0, // Changed to 0 for proper indexing
    pageSize: externalPageSize,
    onPageSizeChange
}) {
    const { t } = useTranslation();
    const [internalPageSize, setInternalPageSize] = useState(10);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [openUp, setOpenUp] = useState(false);
    const buttonRef = useRef(null);

    // Use external pageSize if provided, otherwise use internal state
    const pageSize = externalPageSize !== undefined ? externalPageSize : internalPageSize;

    const pageSizes = [10, 20, 50, 100];
    const visiblePages = getVisiblePages(currentPage, totalPages);

    // Dropdown open direction
    useEffect(() => {
        if (dropdownOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenUp(spaceBelow < 200 && spaceAbove > spaceBelow);
        }
    }, [dropdownOpen]);

    // Scroll to top on page/pageSize change
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [currentPage, pageSize]);

    const handlePageSizeSelect = (size) => {
        if (onPageSizeChange) {
            onPageSizeChange(size);
        } else {
            setInternalPageSize(size);
        }
        onPageChange(1, size); // reset to page 1
        setDropdownOpen(false);
    };

    const handlePrevious = () => currentPage > 1 && onPageChange(currentPage - 1, pageSize);
    const handleNext = () => currentPage < totalPages && onPageChange(currentPage + 1, pageSize);

    // FIXED: Correct calculation for startIndex and endIndex
    const startIndex = totalItems > 0 ? startNumber + (currentPage - 1) * pageSize + 1 : 0;
    const endIndex = Math.min(startIndex + pageSize - 1, startNumber + totalItems);

    return (
        <div className="pagination-container">
            {/* Results Info */}
            <div className="results-info-main">
                <span className="results-info">
                    {t("Showing")} {startIndex} - {endIndex} {t("of")} {totalItems} {t("files")}
                </span>
            </div>

            {/* Page Controls */}
            <div className="page-size-wrapper-main">
                {/* Page Size Dropdown */}
                <div className="custom-select-wrapper">
                    <button
                        ref={buttonRef}
                        className="custom-select-button"
                        onClick={() => setDropdownOpen(!dropdownOpen)}
                    >
                        {pageSize} &nbsp;
                        <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                            <path
                                fill="#374151"
                                fillRule="evenodd"
                                d={
                                    dropdownOpen
                                        ? "M12.248 8.237a.25.25 0 0 0-.354 0L5.53 14.601a.75.75 0 1 1-1.06-1.06l6.363-6.364a1.75 1.75 0 0 1 2.475 0l6.364 6.364a.75.75 0 0 1-1.06 1.06z"
                                        : "M4.47 9.4a.75.75 0 0 1 1.06 0l6.364 6.364a.25.25 0 0 0 .354 0L18.612 9.4a.75.75 0 0 1 1.06 1.06l-6.364 6.364a1.75 1.75 0 0 1-2.475 0L4.47 10.46a.75.75 0 0 1 0-1.06"
                                }
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>

                    {dropdownOpen && (
                        <ul className={`custom-select-options ${openUp ? "open-up" : "open-down"}`} ref={buttonRef}>
                            {pageSizes.map((size) => (
                                <li
                                    key={size}
                                    onClick={() => handlePageSizeSelect(size)}
                                    className={size === pageSize ? "selected" : ""}
                                >
                                    {size}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Previous Button */}
                <button
                    className="pagination-button"
                    onClick={handlePrevious}
                    disabled={currentPage === 1}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 20 20">
                        <path fill="currentColor" d="m4 10l9 9l1.4-1.5L7 10l7.4-7.5L13 1z" />
                    </svg>
                    <span>{t("Previous")}</span>
                </button>

                {/* Page Numbers */}
                <div className="pagination-numbers">
                    {visiblePages.map((p, index) =>
                        p === "..." ? (
                            <span key={`ellipsis-${index}`} className="ellipsis">...</span>
                        ) : (
                            <button
                                key={p}
                                onClick={() => onPageChange(p, pageSize)}
                                className={p === currentPage ? "active" : ""}
                            >
                                {p}
                            </button>
                        )
                    )}
                </div>

                {/* Next Button */}
                <button
                    className="pagination-button"
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                >
                    <span>{t("Next")}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 20 20">
                        <path fill="currentColor" d="M7 1L5.6 2.5L13 10l-7.4 7.5L7 19l9-9z" />
                    </svg>
                </button>
            </div>
        </div>
    );
}